import type { ReactNode } from 'react';
import { cn } from './utils';

export interface CardProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly padding?: 'none' | 'sm' | 'md' | 'lg';
}

const PADDING = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
} as const;

export function Card({ children, className, padding = 'md' }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-surface shadow-sm',
        PADDING[padding],
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
  /** Nível do heading. Manter a hierarquia h1→h2→h3 é requisito WCAG 1.3.1. */
  headingLevel = 2,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly className?: string;
  readonly headingLevel?: 2 | 3 | 4;
}) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0 flex-1">
        <Heading className="text-base font-semibold text-content">{title}</Heading>
        {description ? (
          <p className="mt-1 text-sm text-content-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardBody({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <div className={cn('mt-4', className)}>{children}</div>;
}

export function CardFooter({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <div className={cn('mt-4 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3', className)}>
      {children}
    </div>
  );
}

/**
 * Cartão de métrica do dashboard.
 *
 * O valor é `qc-tabular` e recebe um rótulo textual completo: o número grande
 * sozinho não tem significado para quem navega por headings. O `<a>` opcional
 * transforma o cartão inteiro em atalho para a lista já filtrada.
 */
export function MetricCard({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  onClick,
  className,
}: {
  readonly label: string;
  readonly value: number | string;
  readonly hint?: string;
  readonly tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  readonly icon?: ReactNode;
  readonly onClick?: () => void;
  readonly className?: string;
}) {
  const toneStyles = {
    neutral: 'border-border',
    success: 'border-l-4 border-l-success border-border',
    warning: 'border-l-4 border-l-warning border-border',
    danger: 'border-l-4 border-l-danger border-border',
    info: 'border-l-4 border-l-info border-border',
  } as const;

  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-content-muted">
          {label}
        </span>
        {icon ? (
          <span aria-hidden="true" className="text-content-subtle">
            {icon}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-3xl font-semibold text-content qc-tabular">{value}</p>
      {hint ? <p className="mt-1 text-xs text-content-subtle">{hint}</p> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'rounded-lg border bg-surface p-4 text-left shadow-sm transition-colors',
          'hover:bg-surface-sunken focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          toneStyles[tone],
          className,
        )}
      >
        {content}
        <span className="qc-sr-only">Abrir a lista filtrada por {label}.</span>
      </button>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-surface p-4 shadow-sm', toneStyles[tone], className)}>
      {content}
    </div>
  );
}
